/**
 * Chấm điểm SEO 2026 cho bài viết website (DEC-720, anh Tiến 01/11/2026): "bài viết chuẩn seo 2026, có backlink nội bộ,
 * có từ khóa, có cấu trúc AI search; không quá dài nhưng đầy đủ, súc tích; đạt 90 điểm mới được update; có CTA, chữ ký".
 * Hàm thuần, chạy giống nhau ở máy NV (chấm ngay khi gõ) và máy chủ (chặn đăng dưới 90 điểm). Thang 100 điểm.
 */
import { webPostWarnings } from './web-posts';

export const WEB_POST_SEO_MIN = 90;
/** CTA mặc định cuối mỗi bài (GĐ sửa ở Cài đặt của màn Đăng bài và Ảnh). */
/** DEC-780 (anh Tiến 02/11/2026): CTA mặc định = khối thông tin công ty, nhiều dòng, có emoji. Bản cũ (DEC-720) giữ ở WEB_POST_CTA_V1. */
export const WEB_POST_DEFAULT_CTA = [
  '📌 CÔNG TY TNHH XNK THÉP & THIẾT BỊ CÔNG NGHIỆP MINH TIẾN',
  '📍 Trụ Sở Chính: 1097 Huỳnh Văn Lũy, phường Bình Dương, Thành phố Hồ Chí Minh',
  '🏢 Nhà máy: 8845 Đ.Tân Vạn – Mỹ Phước, phường Hòa Lợi, Thành phố Hồ Chí Minh',
  '📞 Tel: (0274) 3 678 769 – (0274) 3 678 770',
  '🔥 Hotline: 0933.160.169 (Mr. Tiến)',
  '📧 Email: sieuthisatthep.net@gmail.com',
  '🌐 Website: https://sieuthisatthep.net – https://thepminhtien.com',
].join('\n');
export const WEB_POST_CTA_V1 =
  'Cần báo giá thép, inox, thiết bị PCCC, vật tư cơ điện? Gọi / Zalo 0933160169 – 0937664114. Thép Minh Tiến báo giá trong 5 phút, giao hàng nhanh mọi miền.';
export const WEB_POST_CTA_MAX = 1000;
/** Tên miền nội bộ: link tới đây mới tính là link nội bộ (và mới được hiện thành link trên web). */
export const WEB_POST_INTERNAL_HOSTS = ['thepminhtien.com', 'sieuthisatthep.net', 'pccc.sieuthisatthep.net'] as const;
export const WEB_POST_FAQ_HEADING = 'Câu hỏi thường gặp';

export interface SeoCheck {
  code: string;
  label: string;
  ok: boolean;
  points: number;
  max: number;
  hint?: string;
}
export interface SeoResult {
  score: number;
  checks: SeoCheck[];
  words: number;
  internalLinks: number;
}

const LINK_RE = /\[([^\]\n]{1,200})\]\((https?:\/\/[^\s)]{1,500})\)/g;
const norm = (s: string) =>
  s
    .normalize('NFC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Link có trỏ về web của công ty không (kể cả www. và trang con). */
export function isInternalUrl(url: string): boolean {
  try {
    const h = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    return (WEB_POST_INTERNAL_HOSTS as readonly string[]).some((d) => h === d);
  } catch {
    return false;
  }
}

/** Từ khóa chính = cụm đầu tiên trong ô Từ khóa (ngăn bởi dấu phẩy). */
export const mainKeyword = (keywords: string) => (keywords.split(/[,;\n]/)[0] ?? '').trim();

export function seoScore(input: { title: string; body: string; keywords: string; metaDescription: string }): SeoResult {
  const title = input.title.trim();
  const body = input.body.replace(/\r/g, '');
  const meta = input.metaDescription.trim();
  const kw = norm(mainKeyword(input.keywords));
  const plain = body.replace(LINK_RE, '$1').replace(/[#*_>`-]/g, ' ');
  const words = norm(plain).split(' ').filter(Boolean).length;
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const firstPara = paragraphs.find((p) => !p.startsWith('#')) ?? '';
  const h2 = body.split('\n').filter((l) => /^##\s+\S/.test(l));
  const faqIdx = h2.findIndex((l) => norm(l).includes(norm(WEB_POST_FAQ_HEADING)));
  const faqPart = faqIdx >= 0 ? body.slice(body.indexOf(h2[faqIdx]!)) : '';
  const faqQs = (faqPart.match(/^\*\*[^*\n]{5,200}\?\*\*/gm) ?? []).length;
  const links = [...body.matchAll(LINK_RE)].map((m) => m[2]!);
  const internalLinks = new Set(links.filter(isInternalUrl)).size;
  const kwHits = kw ? (` ${norm(plain)} `.split(` ${kw} `).length - 1) : 0;
  const density = words && kw ? (kwHits * kw.split(' ').length * 100) / words : 0;
  const textParas = paragraphs.filter((p) => !p.startsWith('#') && !/^[-•*]\s/.test(p));
  const longest = Math.max(0, ...textParas.map((p) => norm(p).split(' ').length));
  const hasList = /^[-•*]\s+\S/m.test(body);
  const leaks = webPostWarnings(`${title}\n${body}\n${meta}`);

  const c = (code: string, label: string, ok: boolean, max: number, hint?: string): SeoCheck => ({ code, label, ok, points: ok ? max : 0, max, hint });
  const checks: SeoCheck[] = [
    c('keyword', 'Có từ khóa chính', kw.length >= 3, 5, 'Gõ từ khóa chính ở ô Từ khóa (cụm đầu tiên)'),
    c('title_len', 'Tiêu đề 30–65 ký tự', title.length >= 30 && title.length <= 65, 8, `Hiện ${title.length} ký tự`),
    c('title_kw', 'Tiêu đề có từ khóa chính', !!kw && norm(title).includes(kw), 10),
    c('intro_kw', 'Đoạn mở đầu có từ khóa và trả lời thẳng (AI search)', !!kw && norm(firstPara).includes(kw) && norm(firstPara).split(' ').length >= 25, 10, 'Đoạn đầu 2–3 câu, nêu ngay ý chính + từ khóa'),
    c('meta', 'Mô tả (meta) 120–160 ký tự, có từ khóa', meta.length >= 120 && meta.length <= 160 && !!kw && norm(meta).includes(kw), 10, `Hiện ${meta.length} ký tự`),
    c('length', 'Độ dài 400–1100 chữ (súc tích, đủ ý)', words >= 400 && words <= 1100, 10, `Hiện ${words} chữ`),
    c('headings', 'Có ít nhất 3 tiêu đề phụ "## "', h2.length >= 3, 8, `Hiện ${h2.length}`),
    c('faq', `Có mục "${WEB_POST_FAQ_HEADING}" với ≥ 2 câu hỏi in đậm (AI search)`, faqQs >= 2, 10, 'Mỗi câu hỏi một dòng dạng **Câu hỏi?** rồi câu trả lời'),
    c('links', 'Có ≥ 2 link nội bộ tới web công ty', internalLinks >= 2, 10, `Hiện ${internalLinks} link`),
    c('density', 'Mật độ từ khóa 0,5–2,5 % (không nhồi)', density >= 0.5 && density <= 2.5, 7, `Hiện ${density.toFixed(1)} %`),
    c('paragraphs', 'Đoạn văn ngắn (≤ 90 chữ / đoạn)', textParas.length > 0 && longest <= 90, 4),
    c('list', 'Có gạch đầu dòng "- " dễ đọc', hasList, 3),
    c('privacy', 'Không lộ giá, SĐT, biển số, MST khách', leaks.length === 0, 5, leaks.map((w) => w.label).join(', ')),
  ];
  const score = checks.reduce((s, x) => s + x.points, 0);
  return { score, checks, words, internalLinks };
}
